import { createContext, useState, useContext, ReactNode } from "react";
import {FADE_DEFAULT} from "@/constants/materializationConstants";
import {DEFAULT_ASPECT_RATIO} from "@/constants";
import {AspectRatio} from "@/reactvideoeditor/pro/types";
import {FilterOption} from "@/types/filterTypes";

// Tipo per i valori ammessi di "mode"


// Tipi per il contesto
interface MaterializationSettingsContextType {
    fade: boolean
    fadeDuration: number
    setFade: (value: boolean) => void
    setFadeDuration: (duration: number) => void
    lCut: boolean
    setLCut: (value: boolean) => void
    lCutOffsets: Record<string, number>
    setLCutOffset: (overlayId: string, idClip: string, value: number) => void
    mergeLCutOffsets: (entries: Record<string, number>) => void
    savedLCutOffsets: Record<string, number>
    setSavedLCutOffsets: (offsets: Record<string, number>) => void
    aspectRatio: AspectRatio,
    setAspectRatio: (aspectRatio: AspectRatio) => void
    resetMaterializationSettings: () => void
    audioTrack: FilterOption | null
    setAudioTrack: (audioTrack: FilterOption | null) => void
}

// Creazione del contesto
const MaterializationSettingsContext = createContext<MaterializationSettingsContextType | undefined>(undefined);

// Hook personalizzato
export const useMaterializationSettings = (): MaterializationSettingsContextType => {
    const context = useContext(MaterializationSettingsContext);
    if (!context) {
        throw new Error("useMaterializationSettings must be used within a MaterializationSettingsProvider");
    }
    return context;
};

// Tipi per le props del provider
interface MaterializationSettingsContextProviderProps {
    children: ReactNode;
}

// Provider
export const MaterializationSettingsProvider: React.FC<MaterializationSettingsContextProviderProps> = ({ children }) => {
    const [fade, setFade] = useState<boolean>(false);
    const [fadeDuration, setFadeDuration] = useState<number>(FADE_DEFAULT);
    const [lCut, setLCut] = useState<boolean>(false);
    const [lCutOffsets, setLCutOffsets] = useState<Record<string, number>>({});
    const [savedLCutOffsets, setSavedLCutOffsets] = useState<Record<string, number>>({});
    const [aspectRatio, setAspectRatio] = useState<AspectRatio>(DEFAULT_ASPECT_RATIO);
    const [audioTrack, setAudioTrack] = useState<FilterOption | null>(null);

    // idClip identifica in modo stabile l'elemento reply (a differenza dell'id dell'overlay,
    // rigenerato ogni volta che la timeline viene ricostruita): salvarlo anche in savedLCutOffsets
    // permette di riapplicare le modifiche fatte in sessione quando gli overlay vengono ricreati
    // (toggle L-cut, passaggio clip <-> key moment) senza perdere l'offset impostato dall'utente.
    const setLCutOffset = (overlayId: string, idClip: string, value: number) => {
        const clampedValue = Math.max(0, value);
        setLCutOffsets(prev => ({...prev, [overlayId]: clampedValue}));
        setSavedLCutOffsets(prev => ({...prev, [idClip]: clampedValue}));
    }

    // Usata quando gli overlay vengono rigenerati (toggle L-cut, passaggio clip <-> key moment):
    // gli offset sono già noti (letti da savedLCutOffsets, keyed by idClip), qui li si registra
    // solo sotto i nuovi id overlay, in un solo aggiornamento invece di uno per replay.
    const mergeLCutOffsets = (entries: Record<string, number>) => {
        if (Object.keys(entries).length === 0) return;
        setLCutOffsets(prev => ({...prev, ...entries}));
    }

    const resetMaterializationSettings = () => {
        setFade(false);
        setFadeDuration(FADE_DEFAULT)
        setLCut(false);
        setLCutOffsets({});
        setSavedLCutOffsets({});
        setAspectRatio(DEFAULT_ASPECT_RATIO);
        setAudioTrack(null)
    }

    return (
        <MaterializationSettingsContext.Provider
            value={{
                fade,
                fadeDuration,
                setFade,
                setFadeDuration,
                lCut,
                setLCut,
                lCutOffsets,
                setLCutOffset,
                mergeLCutOffsets,
                savedLCutOffsets,
                setSavedLCutOffsets,
                aspectRatio,
                setAspectRatio,
                audioTrack,
                setAudioTrack,
                resetMaterializationSettings
            }}
        >
            {children}
        </MaterializationSettingsContext.Provider>
    );
};
